<template>
  <v-dialog
    :fullscreen="$vuetify.breakpoint.smAndDown"
    :value="show"
    content-class="helmi-search-watch-dialog"
    :max-width="step === 1 ? 1024 : 512"
    @input="onClose"
  >
    <v-card>
      <v-window
        v-model="step"
        vertical
        reverse
      >
        <v-window-item :value="1">
          <v-toolbar
            color="secondary"
            dark
            flat
          >
            <v-list
              v-if="!processing && data.person"
              class="ma-0 transparent"
              dense
            >
              <v-list-item class="px-0">
                <v-list-item-avatar color="white secondary--text">
                  {{ getInitials(data.person.name) }}
                </v-list-item-avatar>

                <v-list-item-content>
                  <p
                    class="text-h6 font-weight-bold"
                    v-text="data.person.name"
                  />
                  <v-list-item-subtitle v-text="data.person.phone"/>
                  <v-list-item-subtitle v-text="data.person.email"/>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <v-skeleton-loader
              v-else
              max-width="300"
              type="list-item-avatar-three-line"
            />
            <v-spacer/>
            <v-btn
              v-if="$vuetify.breakpoint.smAndDown"
              class="mr-0 mt-3"
              icon
              small
              @click="$emit('close')"
            >
              <v-icon>close</v-icon>
            </v-btn>

            <template #extension>
              <div class="pb-2 mx-n1">
                <helmi-search-watch-active-filters
                  :filters="watch"
                  class="ma-1"
                  color="white"
                  light
                  small
                />
              </div>
            </template>
          </v-toolbar>

          <v-divider/>

          <v-data-iterator
            v-if="mobile"
            :items="data.hitData || []"
          >
            <template #no-data>
              <v-list-item>
                <span class="caption grey--text">Ei tuloksia</span>
              </v-list-item>
            </template>
            <template #default="props">
              <v-list dense>
                <template v-for="(item, index) in props.items">
                  <v-list-item
                    :key="item.title"
                  >
                    <template #default>
                      <v-list-item-content>
                        <v-list-item-title>
                          <span class="font-weight-black mr-1" v-text="`${item.type}:`"/>
                          {{ item.address }}{{ item.huoneistonro ? ` ${item.huoneistonro}` : '' }}
                          {{ rooms(item, true) }}
                        </v-list-item-title>
                        <v-list-item-subtitle v-if="watch.target_type === 'sales'">
                          <span class="font-weight-bold mr-1">
                            Hinta:
                          </span>
                          {{ (+item.osakevaikiinteisto === 1) ?
                            item.vhinta : item.hinta | currency }}
                        </v-list-item-subtitle>
                        <v-list-item-subtitle v-else>
                          <span class="font-weight-bold mr-1">
                            Vuokra:
                          </span>
                          {{ item.kuukausivuokra | currency }} / kk
                        </v-list-item-subtitle>
                        <v-list-item-subtitle>
                          <span class="text-caption font-weight-bold">
                            Lähetetty:
                          </span>
                          <v-chip
                            :color="item.sent ? 'secondary' : 'grey lighten-2'"
                            dark
                            x-small
                          >
                            {{ item.sent | date }}
                            klo
                            {{ item.sent | date({ type: 'time' }) }}
                          </v-chip>
                        </v-list-item-subtitle>
                      </v-list-item-content>

                      <v-list-item-action>
                        <v-btn
                          :href="`${userDetails.backpath}?kohdeid=${item.id}`"
                          color="primary"
                          target="_blank"
                          small
                          text
                        >
                          Avaa
                        </v-btn>
                      </v-list-item-action>
                    </template>
                  </v-list-item>

                  <v-divider
                    :key="index"
                  />
                </template>
              </v-list>
            </template>
          </v-data-iterator>

          <v-data-table
            v-else
            :headers="headers"
            :items="data.hitData || []"
            :loading="processing || !data.person"
            loading-text="Ladataan..."
            tile
          >
            <template #item.price="{ item }">
              <span v-if="watch.target_type === 'sales'">
                {{ (+item.osakevaikiinteisto === 1) ? item.vhinta : item.hinta | currency }}
              </span>
              <span v-else>{{ item.kuukausivuokra | currency }} / kk</span>
            </template>

            <template #item.roomSize="{ item }">
              {{ rooms(item) }}
            </template>

            <template #item.id="{ item }">
              <a
                :href="`${userDetails.backpath}?kohdeid=${item.id}`"
                target="_blank"
                v-text="'Avaa kohde'"
              />
            </template>

            <template #item.sent="{ item }">
              {{ item.sent | date }}
              klo
              {{ item.sent | date({ type: 'time' }) }}
            </template>
          </v-data-table>

          <v-divider/>

          <v-card-actions class="grey lighten-5">
            <v-btn
              color="error"
              @click="onDeactivate"
            >
              <v-icon left>
                delete
              </v-icon> Poista käytöstä
            </v-btn>
            <v-spacer/>
            <v-btn
              color="primary"
              text
              @click="$emit('close')"
            >
              Sulje
            </v-btn>
          </v-card-actions>
        </v-window-item>

        <v-window-item :value="2">
          <v-card flat>
            <v-card-title class="headline">
              Poistetaanko hakuvahti käytöstä?
            </v-card-title>
            <v-card-text>
              Hakuvahdin tiedot poistuvat ja asiakas ei saa enää viestejä.
            </v-card-text>
            <v-card-actions>
              <v-spacer/>
              <v-btn
                color="primary"
                text
                @click="step = 1"
              >
                Peruuta
              </v-btn>
              <v-btn
                color="primary"
                text
                @click="onDeactivationConfirm"
              >
                Poista käytöstä
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-window-item>

        <v-window-item :value="3">
          <v-card
            flat
          >
            <v-row class="text-center">
              <v-col cols="12" class="pt-6">
                <v-avatar
                  color="error"
                  size="98"
                >
                  <v-icon color="white" size="64">
                    delete
                  </v-icon>
                </v-avatar>
              </v-col>
              <v-col cols="12">
                <p class="text-h6 mb-0">
                  Hakuvahti poistettu käytöstä
                </p>
              </v-col>
            </v-row>
            <v-card-actions>
              <v-spacer/>
              <v-btn
                color="primary"
                text
                @click="onClose"
              >
                Sulje
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-window-item>
      </v-window>
    </v-card>
  </v-dialog>
</template>

<script>
/* eslint-disable no-param-reassign */

import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'HelmiSearchWatchDialog',

  components: {
    'helmi-search-watch-active-filters': () => import('@/components/searchWatch/searchWatchActiveFilters'),
  },

  props: {
    data: {
      type: Object,
      default: () => ({}),
    },

    headers: {
      type: Array,
      default: () => [],
    },

    processing: {
      type: Boolean,
      default: false,
    },

    show: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    deactivationDialog: true,
    item: {},
    selected: null,
    step: 1,
    watch: {},
  }),

  computed: {
    ...mapGetters({
      userDetails: 'userDetails',
    }),

    mobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
  },

  watch: {
    data: {
      async handler(data) {
        if (!data.id) return;

        this.watch = await this.getSearchWatch({ id: data.id, mutate: false });
      },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    ...mapActions({
      getSearchWatch: 'searchWatch/getOne',
      updateSearchWatch: 'searchWatch/update',
    }),

    rooms(item, mobile = false) {
      let roomText = '';
      if (mobile) {
        roomText = ',';
      }
      const lots = ['Tontti', 'Vapaa-ajan tontti', 'Rivitalotontti', 'Omakotitalotontti', 'Metsätila'];
      if (lots.includes(item.type)) {
        roomText = '';
      } else {
        roomText = `${roomText} ${item.roomSize}`;
        if (mobile) {
          roomText = `${roomText} ${item.roomSize > 1 ? 'huonetta' : 'huone'}`;
        }
      }
      return roomText;
    },

    getInitials(name) {
      if (!name) return '';

      return name
        .split(' ')
        .slice(0, 2)
        .map(p => p.slice(0, 1))
        .join('')
        .toUpperCase();
    },

    onClose() {
      this.$emit('close');

      setTimeout(() => {
        this.step = 1;
      }, 500);
    },

    onDeactivate() {
      this.step = 2;
    },

    async onDeactivationConfirm() {
      await this.updateSearchWatch({ data: { id: this.watch.id, active: 0 } });
      this.step = 3;
    },
  },
};
</script>

<style lang="scss">
.helmi-search-watch-dialog {
  .v-toolbar,
  .v-toolbar__extension,
  .v-toolbar__content {
    height: auto !important;
  }

  .v-toolbar__content {
    align-items: start;
  }

  .v-skeleton-loader {
    width: 100%;

    > div {
      background: transparent !important;
      padding: 0;
    }
  }
}
</style>
